import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { FileText, Download, Trash2, MessageSquare, Building2, User, Clock, Loader2, CheckCircle, Filter, Pencil } from 'lucide-react';
import api from '../lib/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'Új', color: 'bg-blue-50 text-blue-700' },
  { value: 'in_progress', label: 'Feldolgozás alatt', color: 'bg-[#C5A059]/10 text-[#C5A059]' },
  { value: 'processed', label: 'Feldolgozva', color: 'bg-green-50 text-green-700' },
];

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [editingDoc, setEditingDoc] = useState(null);
  const [comment, setComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadDocs = useCallback(() => {
    api.get('/admin/documents').then(r => setDocs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const filtered = filter === 'all' ? docs : docs.filter(d => d.status === filter);

  const handleUpdate = async (docId) => {
    setSaving(true);
    try {
      const update = {};
      if (newStatus) update.status = newStatus;
      if (comment !== (editingDoc?.admin_comment || '')) update.admin_comment = comment;
      await api.put(`/admin/documents/${docId}`, update);
      setEditingDoc(null);
      loadDocs();
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Biztosan törli ezt a dokumentumot?')) return;
    await api.delete(`/admin/documents/${docId}`);
    loadDocs();
  };

  const handleDownload = async (docId, filename) => {
    try {
      const resp = await api.get(`/admin/documents/${docId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const statusCounts = {
    all: docs.length,
    new: docs.filter(d => d.status === 'new').length,
    in_progress: docs.filter(d => d.status === 'in_progress').length,
    processed: docs.filter(d => d.status === 'processed').length,
  };

  return (
    <div data-testid="admin-documents">
      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Filter size={14} className="text-[#64748B]" />
        {[
          { key: 'all', label: 'Összes' },
          { key: 'new', label: 'Új' },
          { key: 'in_progress', label: 'Folyamatban' },
          { key: 'processed', label: 'Feldolgozva' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
              filter === f.key
                ? 'bg-[#0B1B3D] text-white'
                : 'bg-[#0B1B3D]/5 text-[#64748B] hover:bg-[#0B1B3D]/10'
            }`}
          >
            {f.label} ({statusCounts[f.key]})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center text-[#64748B]">
          {filter === 'all' ? 'Nincsenek feltöltött dokumentumok.' : 'Nincs ilyen státuszú dokumentum.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(doc => {
            const st = STATUS_OPTIONS.find(s => s.value === doc.status) || STATUS_OPTIONS[0];
            const isEditing = editingDoc?.id === doc.id;

            return (
              <div key={doc.id} data-testid={`admin-doc-${doc.id}`}
                className={`bg-white border rounded-lg ${doc.status === 'new' ? 'border-blue-200' : 'border-[#E2E8F0]'}`}>
                <div className="p-4 flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0B1B3D] truncate">{doc.original_filename}</p>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs text-[#64748B]">
                      <span className="flex items-center gap-1"><Building2 size={10} /> {doc.company_name}</span>
                      <span className="flex items-center gap-1"><User size={10} /> {doc.uploaded_by_name || doc.uploaded_by_email}</span>
                      <span>{new Date(doc.created_at).toLocaleString('hu-HU')}</span>
                      <span>{formatSize(doc.size)}</span>
                      <span className={`font-medium px-2 py-0.5 rounded ${st.color}`}>{st.label}</span>
                    </div>
                    {doc.admin_comment && !isEditing && (
                      <div className="mt-2 flex items-start gap-2 p-2 bg-[#FAFAFA] rounded text-xs text-[#64748B]">
                        <MessageSquare size={11} className="mt-0.5 text-[#C5A059] flex-shrink-0" />
                        <span>{doc.admin_comment}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(doc.id, doc.original_filename)}
                      className="text-[#64748B] hover:text-[#0B1B3D]" title="Letöltés">
                      <Download size={14} />
                    </Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => {
                        if (isEditing) { setEditingDoc(null); }
                        else { setEditingDoc(doc); setComment(doc.admin_comment || ''); setNewStatus(doc.status); setSaveError(''); }
                      }}
                      className="text-[#64748B] hover:text-[#0B1B3D]" title="Szerkesztés">
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(doc.id)}
                      className="text-[#64748B] hover:text-red-600" title="Törlés">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                {/* Edit panel */}
                {isEditing && (
                  <div className="px-4 pb-4 pt-2 border-t border-[#E2E8F0] bg-[#FAFAFA]/50 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-[#1A1F2C]">Státusz</label>
                      <div className="flex gap-2 mt-1">
                        {STATUS_OPTIONS.map(opt => (
                          <button key={opt.value}
                            onClick={() => setNewStatus(opt.value)}
                            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${
                              newStatus === opt.value ? 'bg-[#0B1B3D] text-white' : 'bg-white border border-[#E2E8F0] text-[#64748B]'
                            }`}
                          >{opt.label}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-[#1A1F2C]">Megjegyzés az ügyfélnek</label>
                      <Textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        rows={2}
                        placeholder="Opcionális megjegyzés..."
                        className="mt-1 border-[#E2E8F0] text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdate(doc.id)} disabled={saving}
                        className="bg-[#0B1B3D] hover:bg-[#162B56] text-white text-xs">
                        {saving ? 'Mentés...' : 'Mentés'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingDoc(null)}
                        className="text-xs border-[#E2E8F0]">Mégse</Button>
                    </div>
                    {saveError && <p className="text-xs text-red-500">{saveError}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
